/** Тънка обвивка около fetch. Сесията пътува с бисквитка, не с хедър. */

async function request(method, path, body) {
  const res = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers: {
      ...(body ? { 'content-type': 'application/json' } : {}),
      // Кодирано, защото името може да е на кирилица („Каса 1"), а в
      // HTTP заглавие такива знаци не се побират и fetch хвърля.
      'x-workstation': encodeURIComponent(workstation()),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (res.status === 204) return null;

  const text = await res.text();
  let data;
  try { data = text ? JSON.parse(text) : null; } catch { data = text; }

  if (!res.ok) {
    /*
     * Сесията е затворена отсреща — друго влизане със същия профил или
     * спрян достъп. Приложението се връща на входа със съобщението,
     * вместо всеки екран да показва своя грешка.
     */
    if ((res.status === 401 && (data?.code === 'session_replaced' || data?.code === 'inactive'))
      || (res.status === 403 && data?.code === 'device_required' && !path.startsWith('/api/auth/login'))) {
      window.dispatchEvent(new CustomEvent('tefter:signed-out', { detail: data.error }));
    }
    const err = new Error(data?.error ?? `Грешка ${res.status}`);
    err.status = res.status;
    err.details = data?.details;
    throw err;
  }
  return data;
}

export const api = {
  get: (path) => request('GET', path),
  post: (path, body) => request('POST', path, body),
  patch: (path, body) => request('PATCH', path, body),
  put: (path, body) => request('PUT', path, body),
  del: (path) => request('DELETE', path),
};

/** Име на работното място — попада в одитния дневник. */
function workstation() {
  let name = localStorage.getItem('sklad_workstation');
  if (!name) {
    name = `${location.hostname || 'браузър'}`;
    localStorage.setItem('sklad_workstation', name);
  }
  return name;
}

/** Съставя адрес със заявка, като пропуска празните стойности. */
export function withQuery(path, params) {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value === undefined || value === null || value === '' || value === false) continue;
    qs.set(key, String(value));
  }
  const s = qs.toString();
  return s ? `${path}?${s}` : path;
}

// ---------- форматиране ----------

const CURRENCY_SYMBOL = { EUR: '€', BGN: 'лв.', USD: '$' };

export function money(value, currency = 'EUR') {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `${n.toLocaleString('bg-BG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${CURRENCY_SYMBOL[currency] ?? currency}`;
}

/**
 * Единичната цена без ДДС по един ред от документ.
 *
 * Смята се от данъчната основа на реда, а не от колоната „цена": тя пази
 * различни неща — сумата С ДДС при продажба и при доставките, пренесени
 * от старата програма, и без ДДС при новите. Основата е вярна навсякъде.
 *
 * Показва се с два знака, но ако при тях количеството по цената не дава
 * стойността на реда, се минава на четири. Иначе на фактурата излиза
 * сметка, която не се затваря: 3 × 8,33 = 24,99 при стойност 24,98.
 */
export function unitPrice(item, currency = 'EUR') {
  const qty = Number(item?.quantity ?? 0);
  const net = Number(item?.line_subtotal ?? 0);
  if (!qty) return money(item?.price, currency);

  const exact = net / qty;
  const two = Math.round(exact * 100) / 100;
  const closes = Math.abs(two * qty - net) < 0.005;
  const n = closes ? two : Math.round(exact * 10000) / 10000;

  return `${n.toLocaleString('bg-BG', {
    minimumFractionDigits: 2, maximumFractionDigits: closes ? 2 : 4,
  })} ${CURRENCY_SYMBOL[currency] ?? currency}`;
}

/**
 * Проверява контролната цифра на ЕИК/Булстат — огледало на
 * `server/src/services/companyLookup.js`. Тук е само за да покаже
 * веднага, преди заявка към сървъра, че въведеното няма как да е вярно —
 * истинската проверка си остава на сървъра.
 */
export function isValidEik(value) {
  const eik = String(value ?? '').trim();
  if (!/^\d{9}$|^\d{13}$/.test(eik)) return false;

  const digits = [...eik].map(Number);
  const check9 = eikChecksum(digits.slice(0, 8), [1, 2, 3, 4, 5, 6, 7, 8], [3, 4, 5, 6, 7, 8, 9, 10]);
  if (check9 !== digits[8]) return false;
  if (eik.length === 9) return true;

  const check13 = eikChecksum(digits.slice(8, 12), [2, 7, 3, 5], [4, 9, 1, 6]);
  return check13 === digits[12];
}

function eikChecksum(digits, weights, fallbackWeights) {
  const sum = (w) => digits.reduce((acc, d, i) => acc + d * w[i], 0);
  let rest = sum(weights) % 11;
  if (rest === 10) rest = sum(fallbackWeights) % 11;
  return rest === 10 ? 0 : rest;
}

export function quantity(value) {
  if (value === null || value === undefined) return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  // Показваме толкова знака, колкото реално са нужни.
  return n.toLocaleString('bg-BG', { maximumFractionDigits: 4 });
}

/**
 * Дата (YYYY-MM-DD) по МЕСТНО време, не по UTC.
 *
 * `new Date().toISOString().slice(0, 10)` изглежда безобидно, но
 * `toISOString` винаги дава UTC — в България (UTC+2/+3) всеки час между
 * полунощ и изгрев се чете като ПРЕДИШНИЯ ден. „Днес"/„текущ месец" не
 * бива да зависи от часа на деня, затова тук се четат местните
 * getFullYear/getMonth/getDate, не UTC representация.
 */
export function isoDate(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** „2026-08" -> „август 2026". Справките групират по месец точно в този вид. */
export function monthLabel(yyyyMm) {
  if (!yyyyMm) return '—';
  const [g, m] = String(yyyyMm).split('-');
  return new Date(Number(g), Number(m) - 1, 1)
    .toLocaleDateString('bg-BG', { month: 'long', year: 'numeric' });
}

export function date(value) {
  if (!value) return '—';
  const s = String(value);
  const d = s.length > 10 ? new Date(s) : new Date(`${s}T00:00:00`);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString('bg-BG');
}

export function dateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('bg-BG', { dateStyle: 'short', timeStyle: 'short' });
}

export const DOC_TYPE_LABEL = {
  sale: 'Продажба',
  purchase: 'Доставка',
  sale_return: 'Връщане от клиент',
  purchase_return: 'Връщане към доставчик',
  transfer: 'Прехвърляне',
  revision: 'Ревизия',
  write_off: 'Брак',
  other_income: 'Друг приход',
  other_expense: 'Друг разход',
};

export const STATUS_LABEL = {
  // Продажбата се открива при първата стока и дотогава е започната, а не
  // чернова: номерът ѝ вече е раздаден и назад няма връщане.
  draft: 'Започната',
  confirmed: 'Записан',
  stornoed: 'Сторниран',
  storno: 'Сторно',
  void: 'Анулирана',
};

export const STATUS_CLASS = {
  draft: 'warn',
  confirmed: 'ok',
  stornoed: 'danger',
  storno: 'warn',
  void: 'muted',
};

/**
 * Начините на плащане. Поведението им живее в базата (`payment_methods`)
 * и се чете от `/api/payment-methods`; тук са само имената, за да може
 * списък да се покаже, преди отговорът да е дошъл.
 */
export const PAYMENT_LABEL = {
  cash: 'В брой',
  cash_deferred: 'В брой на разсрочено',
  card: 'Карта ПОС',
  bank: 'По банков път',
  postal: 'Пощенски паричен превод',
  gift: 'Подарък',
};

export const PAYMENT_SHORT = {
  cash: 'в брой',
  cash_deferred: 'в брой разсрочено',
  card: 'Карта ПОС',
  bank: 'банка',
  postal: 'ППП',
};
